import LogoutIcon from "@mui/icons-material/Logout";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Fade,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Stack,
	type StackProps,
	type SxProps,
	Typography,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../../store/auth-store";
import type { FCWithChildren } from "../../../types";
import type { Schemas } from "../../apis/norsk-gassnett-api-hooks";
import { useAuthContext } from "../../contexts/auth-context";
import { useIdentityContext } from "../../contexts/identity-context";
import { stringToColor } from "../../helpers/utils";
import Logo from "../atomics/logo";
import MUIIcon from "../atomics/mui-icon";
import StaticButton from "../atomics/static-button";

interface GridElementProps {
	xs?: number;
	sm?: number;
	lg?: number;
	sx?: SxProps;
	justifyContent?: StackProps["justifyContent"];
}

const GridElement: FCWithChildren<GridElementProps> = ({
	children,
	xs,
	sm,
	lg,
	sx,
	justifyContent,
}) => (
	<Grid size={{ xs, sm, lg }} sx={sx}>
		<Stack
			direction="row"
			justifyContent={justifyContent || "center"}
			alignItems="center"
		>
			{children}
		</Stack>
	</Grid>
);

const shortenName = (name: string) => {
	const [valid, first, last] = name.trim().match(/^(\S).*\s(\S)\S*$/i) ?? [];
	if (!valid) return name[0]?.toUpperCase() ?? "";
	const combined = first + last;
	return combined.toUpperCase();
};

const SwitchUserDialog = ({
	setDialogOpen,
	dialogOpen,
}: {
	dialogOpen: boolean;
	setDialogOpen: (value: boolean) => void;
}) => {
	const [loadingBcId, setLoadingBcId] = useState<string | null>(null);
	const queryClient = useQueryClient();
	const { switchIdentity } = useAuthContext();
	const { identity, linked, identityMutation } = useIdentityContext();

	const { t } = useTranslation();

	const handleSwitchUser = async (bcId: string) => {
		setLoadingBcId(bcId);
		try {
			await switchIdentity(bcId);
			queryClient.clear();
			setDialogOpen(false);
		} finally {
			setLoadingBcId(null);
		}
	};

	const avatarSize = 56;

	const avatarPropsFromName = (
		name: string,
		extra?: Schemas["MeResponseBody"]["linked"][number],
	) => {
		return {
			sx: {
				bgcolor:
					identityMutation.isPending && extra && extra.bcId !== loadingBcId
						? "action.disabled"
						: stringToColor(name),
				width: avatarSize,
				height: avatarSize,
			},
			children:
				extra && extra.bcId === loadingBcId ? (
					<CircularProgress size="1.3em" sx={{ color: "background.paper" }} />
				) : (
					shortenName(name)
				),
		};
	};

	return (
		<Dialog
			open={dialogOpen}
			onClose={() => setDialogOpen(false)}
			// maxWidth="sm"
			fullWidth
			sx={{
				"& .MuiDialog-paper": {
					px: 2,
					mb: 0,
				},
			}}
		>
			<DialogTitle variant="h4" textAlign="center" mb={1}>
				{t("switch.title")}
			</DialogTitle>
			<DialogContent sx={{ pb: 0 }}>
				<Stack direction="row" alignItems="center" gap={2} width="100%">
					<Avatar {...avatarPropsFromName(identity.name)} />
					<Typography>{identity.name}</Typography>
				</Stack>
				<Divider sx={{ my: 2 }} />
				<Stack gap={2}>
					{linked
						.filter((user) => user.bcId !== identity.id)
						.map((user) => (
							<Button
								disabled={identityMutation.isPending}
								key={user.bcId}
								onClick={() => handleSwitchUser(user.bcId)}
								sx={(t) => ({
									fontWeight: t.typography.fontWeightRegular,
									borderRadius: "100vmax 0 0 100vmax",
									p: t.spacing(0, 2, 0, 0),
								})}
								endIcon={<MUIIcon name="swap_horiz" />}
							>
								<Stack direction="row" alignItems="center" gap={2} width="100%">
									<Avatar {...avatarPropsFromName(user.name, user)} />
									<Typography>{user.name}</Typography>
								</Stack>
							</Button>
						))}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setDialogOpen(false)}>Lukk</Button>
			</DialogActions>
		</Dialog>
	);
};

const ProfilePopper = () => {
	const { t } = useTranslation();
	const [dialogOpen, setDialogOpen] = useState(false);
	const { linked } = useIdentityContext();
	const { logout } = useAuthContext();

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const open = Boolean(anchorEl);

	const queryClient = useQueryClient();

	const onLogout = () => {
		logout();
		queryClient.clear();
	};

	const canSwitchUser = linked.length >= 2;

	return (
		<>
			<IconButton onClick={handleClick}>
				<Avatar
					sx={(t) => ({
						bgcolor: t.palette.secondary.main,
						width: { xs: "2rem", sm: "2.3rem" },
						height: { xs: "2rem", sm: "2.3rem" },
					})}
				>
					<MUIIcon
						name="perm_identity"
						sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }}
					/>
				</Avatar>
			</IconButton>
			<Popper open={open} anchorEl={anchorEl} placement="bottom-end" transition>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={350}>
						<Paper
							sx={(t) => ({
								p: 0,
								backgroundColor: t.palette.background.default,
							})}
						>
							<List>
								{canSwitchUser && (
									<>
										<ListItem disablePadding>
											<ListItemButton
												sx={{ py: 2, paddingRight: 4 }}
												onClick={() => setDialogOpen(true)}
											>
												<ListItemIcon>
													<SwapHorizIcon />
												</ListItemIcon>
												<ListItemText primary={t("header.switch_entity")} />
											</ListItemButton>
										</ListItem>
										<Divider />
									</>
								)}
								<ListItem disablePadding>
									<ListItemButton
										sx={{ py: 2, paddingRight: 4 }}
										onClick={onLogout}
									>
										<ListItemIcon>
											<LogoutIcon />
										</ListItemIcon>
										<ListItemText primary={t("header.logout")} />
									</ListItemButton>
								</ListItem>
							</List>
						</Paper>
					</Fade>
				)}
			</Popper>
			<SwitchUserDialog dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} />
		</>
	);
};

const Header = () => {
	const { t } = useTranslation();
	const { token } = useAuthStore();

	return (
		<Box sx={{ bgcolor: "background.paper" }}>
			<StaticButton
				sx={{
					borderRadius: 0,
					width: 1,
					py: 1,
					justifyContent: "flex-start",
					display: { sm: "none" },
				}}
				arrow="before"
				href={t("general.websiteUrl")}
			>
				{t("general.toWebsiteUrlText")}
			</StaticButton>
			<Grid container p={2} alignItems="center" justifyContent="space-between">
				<GridElement sm={4} sx={{ display: { xs: "none", sm: "block" } }}>
					<StaticButton
						transparent
						arrow="before"
						href={t("general.websiteUrl")}
					>
						{t("general.toWebsiteUrlText")}
					</StaticButton>
				</GridElement>
				<GridElement
					xs={token ? 6 : 12}
					sm={4}
					justifyContent={{ xs: token ? "left" : "center", sm: "center" }}
				>
					<Logo height="50px" maxWidth="100%" href="/" />
				</GridElement>
				<GridElement
					xs={6}
					sm={4}
					justifyContent={{ xs: "right", sm: "center" }}
				>
					{token && <ProfilePopper />}
				</GridElement>
			</Grid>
		</Box>
	);
};

export default Header;
